import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { ReportService } from '../../service/data/report.service';
import { Observable, Subscription, of } from 'rxjs';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { BuildingService } from '../../../rent/service/data/building.service';
import { LocalDate } from '../../../core/model/local-date';
import { EntranceService } from '../../../rent/service/data/entrance.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';
import { RentalObjectTypeService } from '../../../rent/service/data/rental-object-type.service';
import { RentalObjectType } from '../../../rent/model/rental-object-type';
import { RentalObjectCategory } from '../../../rent/model/const/enums';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { IAutoCompleteService, AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';

class PremisesRentalObjectTypeSearchService implements IAutoCompleteService<RentalObjectType> {
  private items = new Map<string, RentalObjectType>();

  setItems(items: RentalObjectType[]): void {
    this.items = new Map(items.map(item => [item.id, item]));
  }

  getSelectionItem(id: string, _state: AutoCompleteSelectionState): Observable<AutoCompleteSelectionItem<RentalObjectType>> {
    const item = this.items.get(id);
    return of(item ? this.toSelectionItem(item) : new AutoCompleteSelectionItem('', id, null));
  }

  getSelection(filter: string): Observable<AutoCompleteSelectionItem<RentalObjectType>[]> {
    const searchText = filter === '*' ? '' : filter.trim().toLocaleLowerCase();
    return of(
      Array.from(this.items.values())
        .filter(item => !searchText || item.name.toLocaleLowerCase().includes(searchText))
        .map(item => this.toSelectionItem(item))
    );
  }

  private toSelectionItem(item: RentalObjectType): AutoCompleteSelectionItem<RentalObjectType> {
    return new AutoCompleteSelectionItem(item.name, item.id, item);
  }
}

@Component({
    selector: 'staircase-report',
    templateUrl: './staircase-report.component.html',
    styleUrls: ['./staircase-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StaircaseReportComponent implements OnDestroy, OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _companyService: CompanyService,
    private _dataService: ReportService,
    private _nativeWindow: NativeWindowService,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    buildingService: BuildingService,
    entranceService: EntranceService,
    propertyService: PropertyService,
    private readonly rentalObjectTypeService: RentalObjectTypeService
  ) {
    this.buildingService = buildingService;
    this.entranceService = entranceService;
    this.propertyService = propertyService;
    this.displayDate = LocalDate.fromDate(LocalDate.today());
    const premisesRentalObjectTypeIds = fcf.create('REPORT.PREMISES_RENTAL_OBJECT_TYPES', []);
    premisesRentalObjectTypeIds.disable({ emitEvent: false });
    this.form = fb.group({
      company: fcf.create('COMMON.COMPANY', ''),
      property: fcf.create('COMMON.PROPERTY', null),
      buildingId: fcf.create('COMMON.BUILDING', null),
      entranceId: fcf.create('ENTRANCE.ENTRANCE', null),
      displayDate: fb.group({}),
      floorSortOrder: fcf.create('POST.SORT_ORDER', 'highestFirst'),
      includePremises: fcf.create('REPORT.INCLUDE_PREMISES', false),
      premisesRentalObjectTypeIds,
    });

    this._includePremisesSubscription = this.form.controls['includePremises'].valueChanges.subscribe(includePremises => {
      if (includePremises) {
        premisesRentalObjectTypeIds.enable({ emitEvent: false });
        return;
      }

      premisesRentalObjectTypeIds.setValue([], { emitEvent: false });
      premisesRentalObjectTypeIds.disable({ emitEvent: false });
    });
  }

  displayDate: LocalDate;
  buildingService: BuildingService;
  entranceService: EntranceService;
  propertyService: PropertyService;

  form: FormGroup;
  private _subscription: Subscription;
  private _includePremisesSubscription: Subscription;
  private _rentalObjectTypesSubscription: Subscription;
  companies: { name: string; value: string }[];
  realEstateProperties: { name: string; value: string }[];
  floorSortOrderSelection: { name: string; value: string }[];
  includePremisesSelection: { name: string; value: boolean }[];
  premisesRentalObjectTypeSearchService = new PremisesRentalObjectTypeSearchService();

  ngOnInit() {
    this._companyService.getList().subscribe(data => {
      this.companies = [{ name: 'ALL_TEXT', value: '' }];
      data.forEach(item => {
        this.companies.push({ name: item.toString(), value: item.id });
      });
    });

    this.floorSortOrderSelection = [{ name: 'REPORT.HIGHEST_FIRST', value: 'highestFirst' }];
    this.floorSortOrderSelection.push({ name: 'REPORT.LOWEST_FIRST', value: 'lowestFirst' });
    this.includePremisesSelection = [{ name: 'NO_TEXT', value: false }, { name: 'YES_TEXT', value: true }];

    this._rentalObjectTypesSubscription = this.rentalObjectTypeService.getByCategory(RentalObjectCategory.Premises).subscribe(types => {
      this.premisesRentalObjectTypeSearchService.setItems(types);
    });
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
    if (this._includePremisesSubscription) this._includePremisesSubscription.unsubscribe();
    if (this._rentalObjectTypesSubscription) this._rentalObjectTypesSubscription.unsubscribe();
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;

    this._subscription = this._dataService.getStaircaseListReportWord(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }
}
